'use client';

import { useState, useEffect } from 'react';
import { testimonialService, type Testimonial } from '@/lib/services/testimonialService';
import Icon from '@/components/ui/AppIcon';

const CATEGORY_LABELS: Record<string, string> = {
  cpu: 'CPU 處理器',
  gpu: 'GPU 顯示卡',
  ram: 'RAM 記憶體',
  motherboard: '主機板',
  storage: '儲存裝置',
  cooling: '散熱器',
  psu: '電源供應器',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${
            star <= rating ? 'text-yellow-400' : 'text-gray-300'
          }`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

const FALLBACK_TESTIMONIALS: Testimonial[] = [
  {
    id: '1',
    clientName: 'Michael Chen',
    companyName: 'TechBuild Solutions Ltd',
    companyType: 'System Integrator',
    location: 'Hong Kong',
    projectTitle: 'Enterprise Workstation Deployment',
    projectTitleZh: '企業工作站部署計劃',
    content: 'PC CARGO delivered 200 units of AMD Ryzen 9 processors and ASUS motherboards within 48 hours. Their competitive pricing saved us 18% compared to other suppliers.',
    contentZh: 'PC CARGO 在48小時內交付了200台AMD Ryzen 9處理器和華碩主機板。與其他供應商相比，他們的競爭性定價為我們節省了18%。',
    rating: 5,
    productCategories: ['cpu', 'motherboard'],
    orderVolume: '200 units',
    avatarInitials: 'MC',
    avatarColor: 'bg-orange',
    isFeatured: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: '2',
    clientName: 'Sarah Wong',
    companyName: 'DigiRetail HK',
    companyType: 'Electronics Retailer',
    location: 'Mong Kok, HK',
    projectTitle: 'Gaming PC Component Restocking',
    projectTitleZh: '遊戲電腦零件補貨計劃',
    content: 'We have been sourcing GPU and RAM from PC CARGO for over 2 years. Their real-time stock updates and 24-hour quotation service make inventory planning effortless.',
    contentZh: '我們已向PC CARGO採購顯示卡和記憶體超過2年。他們的即時庫存更新和24小時報價服務讓庫存規劃變得輕鬆。',
    rating: 5,
    productCategories: ['gpu', 'ram'],
    orderVolume: '500+ units/month',
    avatarInitials: 'SW',
    avatarColor: 'bg-navy',
    isFeatured: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: '3',
    clientName: 'James Liu',
    companyName: 'CloudCore Taiwan',
    companyType: 'Data Center Operator',
    location: 'Taipei, Taiwan',
    projectTitle: 'Server Infrastructure Expansion',
    projectTitleZh: '伺服器基礎設施擴展',
    content: 'Ordered Intel Xeon processors and enterprise SSDs for our data center expansion. PC CARGO handled customs documentation professionally and delivered to Taiwan without any issues.',
    contentZh: '為我們的數據中心擴展訂購了英特爾至強處理器和企業級固態硬碟。PC CARGO專業處理了海關文件，並順利交付到台灣。',
    rating: 5,
    productCategories: ['cpu', 'storage'],
    orderVolume: '50 units',
    avatarInitials: 'JL',
    avatarColor: 'bg-lightblue',
    isFeatured: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: '4',
    clientName: 'Linda Zhang',
    companyName: 'Shenzhen PCHub',
    companyType: 'Computer Parts Distributor',
    location: 'Shenzhen, China',
    projectTitle: 'Monthly Component Procurement',
    projectTitleZh: '每月零件採購計劃',
    content: 'PC CARGO is our primary supplier for premium PC components. Their product authenticity guarantee and competitive wholesale pricing have helped us maintain strong margins.',
    contentZh: 'PC CARGO是我們優質電腦零件的主要供應商。他們的產品真實性保證和具競爭力的批發定價幫助我們保持了強勁的利潤率。',
    rating: 5,
    productCategories: ['cpu', 'gpu', 'ram', 'motherboard'],
    orderVolume: '1000+ units/month',
    avatarInitials: 'LZ',
    avatarColor: 'bg-orange',
    isFeatured: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: '5',
    clientName: 'Kevin Park',
    companyName: 'GameZone Korea',
    companyType: 'Gaming Cafe Chain',
    location: 'Seoul, Korea',
    projectTitle: 'Gaming Cafe Hardware Upgrade',
    projectTitleZh: '電競咖啡廳硬件升級',
    content: 'Upgraded 30 gaming stations with RTX 4070 Ti cards sourced from PC CARGO. The bulk pricing was excellent and all cards passed our performance tests.',
    contentZh: '從PC CARGO採購RTX 4070 Ti顯示卡，升級了30台遊戲工作站。批量定價非常優惠，所有顯示卡均通過了我們的性能測試。',
    rating: 5,
    productCategories: ['gpu'],
    orderVolume: '30 units',
    avatarInitials: 'KP',
    avatarColor: 'bg-green-600',
    isFeatured: false,
    createdAt: new Date().toISOString(),
  },
  {
    id: '6',
    clientName: 'David Tanaka',
    companyName: 'Akihabara Systems',
    companyType: 'PC Builder',
    location: 'Tokyo, Japan',
    projectTitle: 'Custom PC Build Components',
    projectTitleZh: '客製化電腦零件採購',
    content: 'Found PC CARGO through a referral and have not looked back. Their stock of latest AMD and Intel CPUs is always fresh, and the pricing beats local distributors.',
    contentZh: '通過推薦找到PC CARGO，從此一直合作。他們最新AMD和Intel CPU的庫存始終充足，定價優於本地分銷商。',
    rating: 4,
    productCategories: ['cpu', 'cooling'],
    orderVolume: '80 units',
    avatarInitials: 'DT',
    avatarColor: 'bg-navy',
    isFeatured: false,
    createdAt: new Date().toISOString(),
  },
];

export default function TestimonialsGrid() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  useEffect(() => {
    async function fetchTestimonials() {
      try {
        const data = await testimonialService.getAll();
        setTestimonials(data?.length > 0 ? data : FALLBACK_TESTIMONIALS);
      } catch {
        setTestimonials(FALLBACK_TESTIMONIALS);
      } finally {
        setLoading(false);
      }
    }
    fetchTestimonials();
  }, []);

  const filters = [
    { id: 'all', label: '全部 All' },
    { id: 'cpu', label: 'CPU' },
    { id: 'gpu', label: 'GPU' },
    { id: 'ram', label: 'RAM' },
    { id: 'motherboard', label: '主機板' },
    { id: 'storage', label: '儲存' },
  ];

  const filtered =
    activeFilter === 'all'
      ? testimonials
      : testimonials?.filter((t) =>
          t?.productCategories?.some((c) => c === activeFilter)
        );

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-black text-3xl md:text-4xl text-navy mb-4">
            客戶真實評價
            <span className="text-orange"> Client Reviews</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto">
            來自亞洲各地B2B買家的真實採購體驗分享
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {filters?.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeFilter === f.id
                  ? 'bg-orange text-white shadow-orange-glow'
                  : 'bg-navy-50 text-navy hover:bg-navy-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-100 rounded-2xl h-64 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered?.map((testimonial) => (
              <div
                key={testimonial?.id}
                className={`relative bg-white border rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col ${
                  testimonial?.isFeatured
                    ? 'border-orange/30 ring-1 ring-orange/20' :'border-gray-100'
                }`}
              >
                {/* Featured badge */}
                {testimonial?.isFeatured && (
                  <div className="absolute -top-3 left-6">
                    <span className="bg-orange text-white text-xs font-bold px-3 py-1 rounded-full">
                      精選案例 Featured
                    </span>
                  </div>
                )}

                {/* Quote icon */}
                <div className="text-orange/20 mb-3">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>

                {/* Content */}
                <p className="text-text-mid text-sm leading-relaxed mb-3 flex-1">
                  {testimonial?.contentZh || testimonial?.content}
                </p>

                {/* Rating */}
                <div className="mb-4">
                  <StarRating rating={testimonial?.rating || 5} />
                </div>

                {/* Product categories */}
                {testimonial?.productCategories?.filter(Boolean)?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {testimonial?.productCategories?.filter(Boolean)?.map((cat) => (
                      <span
                        key={cat}
                        className="bg-navy/5 text-navy text-xs font-medium px-2 py-0.5 rounded-full"
                      >
                        {CATEGORY_LABELS[cat] || cat}
                      </span>
                    ))}
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
                        testimonial?.avatarColor || 'bg-orange'
                      }`}
                    >
                      {testimonial?.avatarInitials}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-navy text-sm truncate">
                        {testimonial?.clientName}
                      </div>
                      <div className="text-text-muted text-xs truncate">
                        {testimonial?.companyName}
                      </div>
                      <div className="flex items-center gap-1 text-text-muted text-xs mt-0.5">
                        <Icon name="MapPinIcon" size={10} />
                        <span>{testimonial?.location}</span>
                      </div>
                    </div>
                    {testimonial?.orderVolume && (
                      <div className="ml-auto text-right flex-shrink-0">
                        <div className="text-xs text-text-muted">採購量</div>
                        <div className="text-xs font-bold text-orange">{testimonial?.orderVolume}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered?.length === 0 && (
          <div className="text-center py-16 text-text-muted">
            <Icon name="ChatBubbleBottomCenterTextIcon" size={48} className="mx-auto mb-4 opacity-30" />
            <p>此分類暫無評價</p>
          </div>
        )}
      </div>
    </section>
  );
}
